import { useMemo } from 'react';
import { GroupByOption, PaginatedResponse } from '@repo/types';
import { WriteOff, WriteOffItem, FlatWriteOffItem } from '../types/writeOffs';

/**
 * Hook to flatten write-off data based on groupBy option
 * @param data - Paginated response containing write-off invoices
 * @param groupBy - Current grouping option (CLIENT or NONE)
 * @returns Flattened array of write-off items ready for table display
 */
export function useFlattenedWriteOffs(
  data: PaginatedResponse<WriteOff> | undefined,
  groupBy: GroupByOption.CLIENT | GroupByOption.NONE
) {
  return useMemo(() => {
    if (!data?.data) return [];
    if (groupBy === GroupByOption.CLIENT) return data.data;

    return data.data.flatMap(invoice => {
      const items = invoice.writeOffs;

      if (items?.length) {
        return items.map((item: WriteOffItem) => ({
          ...invoice,
          ...item,
          writeOffStatus: item.status,
          invoice: invoice,
          _parentInvoice: invoice,
          writeOff: item,
        })) as unknown as FlatWriteOffItem;
      }

      // Fallback for invoices without writeOffs array
      return [
        {
          ...invoice,
          invoice: invoice,
          category: undefined,
          type: undefined,
          amount: invoice.writeOffAmount,
        } as unknown as FlatWriteOffItem,
      ];
    });
  }, [data, groupBy]);
}
